// src/hooks/useRecentJobs.js
// Latest background finalize / export job of each board pack, across all
// board packs. Polls while any of them is still queued or generating.
import { useCallback, useEffect, useState } from "react";
import { apiCall } from "@/api/apiCall";
import { FINISHED_STATUSES, JOBS_CHANGED_EVENT, JOBS_UPDATED_EVENT } from "@/lib/reportJobs";

const POLL_MS = 8000;

// Newest first from the API; keep one job per board pack and job type
const latestPerBoardPack = (jobs, limit) => {
  const seen = new Set();
  return jobs
    .filter((job) => {
      const key = `${job.job_type}|${job.contract}|${job.year}|${job.month}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
};

export const useRecentJobs = (limit = 15) => {
  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiCall("get", "/reports/jobs", { limit: 60 }, null, false, false);
      if (res?.success && Array.isArray(res.data)) setJobs(latestPerBoardPack(res.data, limit));
    } catch {
      /* keep the last list */
    } finally {
      setLoaded(true);
    }
  }, [limit]);

  useEffect(() => {
    load();
  }, [load]);

  // A request was just sent, or the watcher saw a job change
  useEffect(() => {
    window.addEventListener(JOBS_CHANGED_EVENT, load);
    window.addEventListener(JOBS_UPDATED_EVENT, load);
    return () => {
      window.removeEventListener(JOBS_CHANGED_EVENT, load);
      window.removeEventListener(JOBS_UPDATED_EVENT, load);
    };
  }, [load]);

  const activeCount = jobs.filter((job) => !FINISHED_STATUSES.includes(job.status)).length;
  useEffect(() => {
    if (!activeCount) return;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [activeCount, load]);

  return { jobs, loaded, activeCount, reload: load };
};
